import type { AppProps } from 'next/app';
import Script from 'next/script';
import { HeadBase } from '@zg-rentals/next-components';
import { ThemeProvider } from 'styled-components';
import type { Toast } from '@zillow/constellation';
import { ThemeConstellation, ToastProvider } from '@zillow/constellation';
import { ivar, ivarSubset, loader } from '@zillow/font-loading';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';

import '../styles/globals.css';
import { BASE_URL, GRAPHQL_ENDPOINT, ROUTES } from '../constants/urls';
import { REQUESTING_CLIENT } from '../constants/api';
import dynamic from 'next/dynamic';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { bootstrap } from '../bootstrap';
import { ErrorPage } from '../components/Error/ErrorPage';
import { onError } from '@apollo/client/link/error';
import { logError } from '@zg-rentals/log-error';
import { useRouter } from 'next/router';
import { setClient as setCappsClient } from '@zg-rentals/ts-capps-client';
import { setClient as setIdVerificationClient } from '@zg-rentals/ts-id-verification-client';
import { setClient as setRinsClient } from '@zg-rentals/ts-rins-client';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent, trackEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';

const NoSsrApp = dynamic(() => import('../components/App'), { ssr: false });

setCappsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setIdVerificationClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setRinsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      if (error.message === 'downstream dependency failure' && error.path && error.path.length > 0) {
        const httpStatus = error?.extensions?.httpStatus;
        const path = error?.path[0];
        if (httpStatus) {
          bootstrap.monitor.count({ name: 'raApiCall ' + httpStatus });
        }
        if (httpStatus && path) {
          bootstrap.monitor.count({ name: 'raApiCall ' + path + ' ' + httpStatus });
        }
        logError({ error });
      }
    });
  }
});

const zgGraphClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    errorLink,
    createHttpLink({
      credentials: 'include',
      headers: {
        'x-caller-id': REQUESTING_CLIENT,
        'x-z-enable-oauth-conversion': 'true',
      },
      uri: `${BASE_URL}${GRAPHQL_ENDPOINT}`,
    }),
  ]),
});

const toastOptionsFn = ((toast: typeof Toast) => {
  const { appearance } = toast.props;
  if (appearance === 'success' || appearance === 'info') {
    return {
      duration: 3000,
    };
  }
}) as unknown as () => void;

const ApplicationsRenterWeb = (appProps: AppProps) => {
  useBootstrap(() => ({ bootstrap }));

  const router = useRouter();
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useZrmPageViewEvents(trackEvent, router.pathname);

  return (
    <>
      <HeadBase applicationName="applications-renter-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>Rental Applications | Zillow</title>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Open+Sans:400,600,700&amp;display=swap" />
        <style
          type="text/css"
          dangerouslySetInnerHTML={{
            __html: ivar,
          }}
        />
        <style
          type="text/css"
          dangerouslySetInnerHTML={{
            __html: ivarSubset,
          }}
        />
      </HeadBase>

      <Script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
                ${loader}
                ZillowFontLoader.init();
            `,
          }}
        />
      </Script>

      <ThemeProvider theme={ThemeConstellation}>
        <RpErrorBoundary
          fallback={
            <ErrorPage
              errorCode={500}
              errorMessage="Internal server error"
              homePageText="Return to your applications dashboard"
              homePageUrl={ROUTES.INDEX}
            />
          }
        >
          <ApolloProvider client={zgGraphClient}>
            <DefaultQueryProvider
              defaultOptions={{
                queries: {
                  retry: false,
                  refetchOnWindowFocus: false,
                  staleTime: 1000 * 60 * 30, // 30 minutes
                },
              }}
              mutationErrorContext={() => undefined} // Note: disable serializing mutation variables
            >
              <ToastProvider optionsFn={toastOptionsFn}>
                <NoSsrApp appProps={appProps} />
              </ToastProvider>
            </DefaultQueryProvider>
          </ApolloProvider>
        </RpErrorBoundary>
      </ThemeProvider>
    </>
  );
};

export default ApplicationsRenterWeb;
